import api from '@/lib/axios';

export interface AccessPermissions {
  read: boolean;
  write: boolean;
  admin: boolean;
}

export interface AccessPlan {
  code: string;
  name: string;
  is_trial: boolean;
}

export interface AccessResponse {
  modules: string[];
  permissions: Record<string, AccessPermissions>;
  plan: AccessPlan;
}

/**
 * Fetch access information for a workspace
 * 
 * @param workspaceId - The workspace ID
 * @returns Access information including modules, permissions, and plan
 */
export async function fetchWorkspaceAccess(workspaceId: string): Promise<AccessResponse> {
  try {
    const response = await api.get<AccessResponse>(`/workspaces/${workspaceId}/access`);
    // Ensure we always return valid structure
    return {
      modules: Array.isArray(response.data?.modules) ? response.data.modules : [],
      permissions: response.data?.permissions || {},
      plan: response.data?.plan || {
        code: 'trial',
        name: 'Trial',
        is_trial: true,
      },
    };
  } catch (error: any) {
    // If endpoint doesn't exist (404) or other error, return safe defaults
    if (error.response?.status === 404) {
      console.warn(`Access endpoint not available for workspace ${workspaceId}, returning defaults`);
      return {
        modules: [],
        permissions: {},
        plan: {
          code: 'trial',
          name: 'Trial',
          is_trial: true,
        },
      };
    }
    console.error('[fetchWorkspaceAccess] Error:', error);
    // Never throw - always return safe defaults
    return {
      modules: [],
      permissions: {},
      plan: {
        code: 'trial',
        name: 'Trial',
        is_trial: true,
      },
    };
  }
}
